import React from "react";
import { toKm } from "@leafly-com/web-utils";
import classNames from "classnames";
import { useSelector } from "react-redux";

import { getDomainCountryCode } from "redux/selectors/config";
import {
  getCurrentDispensary,
  getIsUnclaimed,
} from "redux/selectors/dispensary";
import { getTotalDispensaryReviewsCount } from "redux/selectors/reviews";
import { dispensaryType } from "utils/dispensaryUtils";
import { getStateOrProvinceLongName } from "utils/getStatesForCountry";

import Actions from "components/Dispensary/DispensaryHeader/Actions";
import BusinessHours from "components/Dispensary/DispensaryHeader/BusinessHours";
import PhotoGallery from "components/Dispensary/DispensaryHeader/PhotoGallery";
import ReviewRating from "components/Dispensary/DispensaryHeader/ReviewRating";
import UnclaimedHeader from "components/Dispensary/DispensaryHeader/UnclaimedHeader";
import DispensaryHeaderFulfillment from "components/Dispensary/DispensaryHeaderFulfillment";
import FollowButton from "components/Dispensary/FollowDispensaryButton";
import Image from "components/Image";

import styles from "./DispensaryHeader.module.css";

const DispensaryHeader: React.FC = () => {
  const countryCode = useSelector(getDomainCountryCode);
  const {
    distanceMi,
    logoUrl,
    name,
    hasDeliveryEnabled,
    pickupEnabled,
    city,
    state,
    retailType,
    tags,
  } = useSelector(getCurrentDispensary);
  const totalReviewsCount = useSelector(getTotalDispensaryReviewsCount);
  const isUnclaimed = useSelector(getIsUnclaimed);

  const distanceKm = toKm(distanceMi)?.toFixed(1);
  const localizedDistance =
    countryCode === "CA"
      ? `${distanceKm} km away`
      : `${distanceMi?.toFixed(1)} miles away`;
  const showDistance = !!distanceMi && (!hasDeliveryEnabled || pickupEnabled);

  if (isUnclaimed) {
    return <UnclaimedHeader />;
  }

  const dispensaryTypeString = dispensaryType(tags);

  return (
    <>
      <div className="container border-b lg:border-b-0 border-deep-green-20 pb-xl">
        <PhotoGallery className="mb-lg" />

        <div className="row justify-between">
          <div className="col lg:col-5 relative">
            <FollowButton className="absolute top-0 right-0" />
            <div
              className={classNames("mb-md", styles["dispensary-header__logo"])}
            >
              <Image
                src={logoUrl}
                sizes={[50, 50, 50, 72, 72, 50]}
                alt={name}
                className="rounded"
                data-testid="dispensary-header__logo"
                disableLazyLoad
              />
            </div>
            <div className="flex items-start">
              <div className="w-full">
                <div className="flex">
                  <div className="uppercase text-xs font-bold border border-light-grey rounded px-sm">
                    {retailType}
                  </div>
                  {dispensaryTypeString && (
                    <div className="pl-xs">
                      <div className="uppercase text-xs font-bold border border-light-grey rounded px-sm">
                        {dispensaryTypeString}
                      </div>
                    </div>
                  )}
                </div>
                <h1 className="heading--m font-extrabold pr-xxl pb-xs pt-xs">
                  {name}
                </h1>
                <div className="flex mb-xs">
                  <div className="text-xs pt-[1.25px]">
                    {city},&nbsp;{getStateOrProvinceLongName(state)}
                  </div>
                  <span
                    className={`mx-xs ${
                      totalReviewsCount > 0 && "after:content-['•']"
                    }`}
                  />
                  <ReviewRating />
                </div>
                <div className="flex w-full justify-between pb-md">
                  {showDistance && (
                    <div
                      className="text-xs"
                      data-testid="dispensary-header__distance"
                    >
                      {localizedDistance}
                    </div>
                  )}
                  <BusinessHours className="mb-sm" />
                </div>
              </div>
            </div>

            <div>
              <Actions className="mb-xl" />
            </div>
          </div>
          <div className="col lg:col-5">
            <DispensaryHeaderFulfillment />
          </div>
        </div>
      </div>
    </>
  );
};

export default DispensaryHeader;
